import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useSidebar } from '../context/SidebarContext';

const STOCK_IMAGE = 'https://media.istockphoto.com/id/938226922/photo/young-stylish-man-in-eyeglasses-sitting-on-chair-isolated-on-grey.jpg?s=612x612&w=0&k=20&c=DtKUcN_L6jNHkGKbsfExszSUQVEVsRbOsc2d_vvc5qc=';

const videoPreviewMap: Record<string, string> = {
  vlog: '/images/tipos_de_videos/vlog.mp4',
  visitando_negocio: '/images/tipos_de_videos/visita_negocio.mp4',
  unboxing: '/images/tipos_de_videos/review.mp4',
  automovil: '/images/tipos_de_videos/auto.mp4',
  podcast: '/images/tipos_de_videos/podcast.mp4',
  entrevista: '/images/tipos_de_videos/entrevista.mp4',
  comparativa: '/images/tipos_de_videos/comparativa.mp4',
};

const videoStyles = [
  { id: 'vlog', name: 'Vlog' },
  { id: 'visitando_negocio', name: 'Visitando tu negocio' },
  { id: 'unboxing', name: 'Unboxing' },
  { id: 'automovil', name: 'Automóvil' },
  { id: 'podcast', name: 'Podcast' },

  { id: 'entrevista', name: 'Entrevista' },
  { id: 'comparativa', name: 'Comparativa' },
];

const ConfigurarVideoInfluencerNuevo: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const idsParam = params.get('ids') || '';
  const ids = useMemo(
    () => idsParam.split(',').map(s => s.trim()).filter(Boolean).filter(s => s !== '0'),
    [idsParam],
  );

  const { sidebarWidth, setMobileTitleOverride } = useSidebar();

  // Restore scroll position when returning to this page
  useLayoutEffect(() => {
    const saved = sessionStorage.getItem('influencer-nuevo-scroll');
    if (saved) {
      const y = parseInt(saved, 10);
      window.scrollTo(0, y);
      sessionStorage.removeItem('influencer-nuevo-scroll');
    }
  }, []);

  // Reverse hero animation state — read from sessionStorage (set by Detalle page on back)
  const desktopCardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const mobileCardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [reverseHeroAnimating, setReverseHeroAnimating] = useState(false);
  const [reverseHeroPhase, setReverseHeroPhase] = useState<'start' | 'end'>('start');
  const reverseEndRect = useRef<{ top: number; left: number; width: number; height: number } | null>(null);
  const reverseHeroRectRef = useRef<{ top: number; left: number; width: number; height: number } | null>(null);
  const reverseStyleIdRef = useRef<string | null>(null);

  useEffect(() => {
    setMobileTitleOverride(' ');
    // Hide mobile header border on this page
    const header = document.querySelector('header.fixed.md\\:hidden') as HTMLElement | null;
    if (header) header.style.borderBottomColor = 'transparent';
    return () => {
      setMobileTitleOverride(null);
      if (header) header.style.borderBottomColor = '';
    };
  }, [setMobileTitleOverride]);

  // Reverse hero: check sessionStorage on every navigation (location.key changes)
  useLayoutEffect(() => {
    const raw = sessionStorage.getItem('influencer-reverse-hero');
    if (!raw) return;
    sessionStorage.removeItem('influencer-reverse-hero');
    let parsed: any;
    try { parsed = JSON.parse(raw); } catch { return; }
    const rect = parsed?.reverseHeroRect;
    const sid = parsed?.reverseStyleId;
    if (!rect || !sid) return;

    reverseHeroRectRef.current = rect;
    reverseStyleIdRef.current = sid;
    setReverseHeroPhase('start');
    setReverseHeroAnimating(true);

    requestAnimationFrame(() => requestAnimationFrame(() => {
      const isMobile = window.innerWidth < 640;
      const refs = isMobile ? mobileCardRefs : desktopCardRefs;
      const card = refs.current[sid];
      if (!card) {
        setReverseHeroAnimating(false);
        return;
      }
      const r = card.getBoundingClientRect();
      reverseEndRect.current = { top: r.top, left: r.left, width: r.width, height: r.height };
      setReverseHeroPhase('end');
      setTimeout(() => setReverseHeroAnimating(false), 420);
    }));
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStyleClick = (styleId: string, e: React.MouseEvent<HTMLDivElement>) => {
    // Save scroll position before navigating
    sessionStorage.setItem('influencer-nuevo-scroll', String(window.scrollY));
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const qp = new URLSearchParams();
    if (ids.length > 0) qp.set('ids', ids.join(','));
    qp.set('style', styleId);
    navigate(`/redes-sociales/crear-anuncio/configurar-video-influencer-detalle?${qp.toString()}`, {
      state: { heroRect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height } },
    });
  };

  return (
    <div className="relative">
      <div className="fixed inset-0 bg-white z-0" />
      <div className="relative z-10">
      {/* Desktop floating back button */}
      <div className="hidden sm:block fixed top-0 right-0 z-40" style={{ left: sidebarWidth }}>
        <div className="max-w-6xl mx-auto px-8 h-14 flex items-center">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1 text-[#1644EB] hover:opacity-70 transition-opacity text-[15px] font-normal"
          >
            <ChevronLeft className="w-5 h-5" />
            <span>Atrás</span>
          </button>
        </div>
      </div>

      {/* Desktop layout */}
      <div className="hidden sm:block max-w-6xl mx-auto px-8 pt-[72px] pb-4">
        <p className="text-[17px] font-semibold text-brand-black/90 mb-4">Elige el tipo de video</p>
        <div>
          <div className="grid grid-cols-4 gap-3">
            {videoStyles.map((style) => (
              <div
                key={style.id}
                ref={(el) => { desktopCardRefs.current[style.id] = el; }}
                onClick={(e) => handleStyleClick(style.id, e)}
                className="group relative rounded-lg overflow-hidden cursor-pointer"
              >
                <div className="aspect-[4/5] relative bg-neutral-100" style={{ opacity: reverseHeroAnimating && reverseStyleIdRef.current === style.id ? 0 : 1 }}>
                  {videoPreviewMap[style.id] ? (
                    <video
                      src={videoPreviewMap[style.id]}
                      muted
                      autoPlay
                      loop
                      playsInline
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  ) : (
                    <img
                      src={STOCK_IMAGE}
                      alt={style.name}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                  )}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent pt-10 pb-3 px-3 pointer-events-none">
                    <p className="text-[13px] font-medium text-white truncate">{style.name}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile layout */}
      <div className="sm:hidden px-4 pt-2 pb-4">
        <p className="text-[17px] font-semibold text-brand-black/90 mt-4 mb-4">Elige el tipo de video</p>
        <div>
          <div className="grid grid-cols-2 gap-2">
            {videoStyles.map((style) => (
              <div
                key={style.id}
                ref={(el) => { mobileCardRefs.current[style.id] = el; }}
                onClick={(e) => handleStyleClick(style.id, e)}
                className="group relative rounded-lg overflow-hidden cursor-pointer"
              >
                <div className="aspect-[4/5] relative bg-neutral-100" style={{ opacity: reverseHeroAnimating && reverseStyleIdRef.current === style.id ? 0 : 1 }}>
                  {videoPreviewMap[style.id] ? (
                    <video
                      src={videoPreviewMap[style.id]}
                      muted
                      autoPlay
                      loop
                      playsInline
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <img
                      src={STOCK_IMAGE}
                      alt={style.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  )}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent pt-10 pb-3 px-3 pointer-events-none">
                    <p className="text-[13px] font-medium text-white truncate">{style.name}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Reverse hero animation overlay */}
      {reverseHeroAnimating && reverseHeroRectRef.current && (() => {
        const isEnd = reverseHeroPhase === 'end';
        const spring = 'cubic-bezier(0.2, 0.9, 0.3, 1)';
        const transition = `top 400ms ${spring}, left 400ms ${spring}, width 400ms ${spring}, height 400ms ${spring}`;
        const end = reverseEndRect.current;

        return (
          <div
            className="pointer-events-none"
            style={{
              position: 'fixed',
              zIndex: 50,
              overflow: 'hidden',
              borderRadius: 16,
              top: isEnd && end ? end.top : reverseHeroRectRef.current!.top,
              left: isEnd && end ? end.left : reverseHeroRectRef.current!.left,
              width: isEnd && end ? end.width : reverseHeroRectRef.current!.width,
              height: isEnd && end ? end.height : reverseHeroRectRef.current!.height,
              transition: isEnd ? transition : 'none',
              willChange: 'top, left, width, height',
            }}
          >
            {reverseStyleIdRef.current && videoPreviewMap[reverseStyleIdRef.current] ? (
              <video
                src={videoPreviewMap[reverseStyleIdRef.current]}
                muted
                autoPlay
                loop
                playsInline
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            ) : (
              <img
                src={STOCK_IMAGE}
                alt=""
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            )}
          </div>
        );
      })()}
      </div>
    </div>
  );
};

export default ConfigurarVideoInfluencerNuevo;
